import type { DateTime } from "luxon";
import {
  createJournal,
  isValid as isValidJournal,
  type Journal,
  type RegistrationJournal,
  type ResultJournal,
} from "./journal";
import type { MailJob } from "./mailJob";
import type { Message } from "./message";
import type { IProduct } from "./product";

export type JournalStatusDetails = {
  report?: string;
  shortStatus: string;
  status: string;
  sentTo: string;
  sentAt: string;
};
export type JournalStatus = JournalStatusDetails;
export type JournalGroupDetailedStatus = {
  leftToDo: string[];
  leftToDoFinal: string[];
  registrationCompleted: boolean;
  waitingEmail: boolean;
  waitingReport: boolean;
  paused: boolean;
  blockSend: boolean;
  customerRegistrationMissing: boolean;
  nextSteps: string[];
  journals: JournalDetailedStatus[];
  analysisGroupInstances: AnalysisGroupInstanceDetailedStatus[];
};

export type JournalDetailedStatus = {
  id: number;
  labnumber?: number;
  status?: string;
  missingRegistrationFields: { key: string; label: string }[];
};

export type AnalysisGroupInstanceDetailedStatus = {
  id: number;
  analysisGroupId: number;
  code: string;
  name: string;
  status: string;
  nextSteps: string[];
  waitingFor?: string;
};
export interface JournalGroupMeta {
  sentToNorfor?: boolean;
  sentToPrevet?: boolean;
  sentToGod?: boolean;
  projectTag?: string;
  mailSelectionStored?: boolean;
  [key: string]: string | DateTime | boolean | undefined;
}

export type GeneratedReport = {
  id: number;
  preliminary: boolean;
  updatedAt: string;
  fileName: string;
  link: string;
};

export interface JournalGroup<T extends Journal = Journal> {
  id?: number;
  loaded?: boolean;
  journals?: T[];
  status?: JournalStatus;
  detailedStatus?: JournalGroupDetailedStatus;
  customerId?: number;
  journalGroupNumber?: string;
  note?: string;
  notes?: Message[];
  customerNote?: string;
  customerNotes?: Message[];
  followup?: boolean;
  followupJournal?: string;
  followupJournalId?: number;
  productFamilyId?: number;
  productId?: number;
  invoice?: { [code: string]: boolean };
  force?: { [code: string]: boolean };
  productType?: IProduct;
  mailJobs?: MailJob[];
  collectedAt?: string;
  meta?: JournalGroupMeta;
  veterinarianId?: number;
  deleted?: boolean;
  invoiceGroups?: { id: number; code: string }[];
  invoiceBasisId?: number;
  invoiceId?: number;
  quickPackageId?: number;
  blockSend?: boolean;
}

export type RegistrationJournalGroup = JournalGroup<RegistrationJournal>;
export type ResultJournalGroup = JournalGroup<ResultJournal>;

export function createJournalGroup<T extends Journal>(
  journalGroup?: JournalGroup<T>,
): JournalGroup<T> {
  const journals = journalGroup?.journals?.map((j) => createJournal(j));
  return { ...journalGroup, journals };
}

export function isValid(journalGroup: RegistrationJournalGroup) {
  return (
    !!journalGroup.journals?.length &&
    !journalGroup.journals?.some((j) => !isValidJournal(j))
  );
}

export function readMeta<K extends keyof JournalGroupMeta>(
  journalGroup: JournalGroup,
  key: K,
): JournalGroupMeta[K] {
  if (journalGroup.meta?.[key]) {
    return journalGroup.meta[key];
  }
  return undefined;
}
